import { ChangeDetectionStrategy, Component, TemplateRef, ViewChild } from '@angular/core';
import { forkJoin } from 'rxjs';
import { Article } from '../../../rent/model/article';
import { Company } from '../../../rent/model/company';
import { ArticleService } from '../../../rent/service/data/article.service';
import { ContactService } from '../../../rent/service/data/contact.service';
import { PagedObjectManagerComponent } from '../../../core/component/paged-object-manager/paged-object-manager';
import { ColumnVisibility } from '../../../core/model/column-definition';
import { Command, CommandBase, FilteredCommand } from '../../../core/model/command';
import { FilterDefinitionItem, FilterDefinitionItemLocalDate } from '../../../core/model/filter-definition-item';
import { CommandDispatcher } from '../../../core/service/command/command-dispatcher.service';
import { StandardCommandHandlerResolver } from '../../../core/service/command/standard-command-handler-resolver.service';
import { ColumnDefinitionFactory } from '../../../core/service/factory/column-definition-factory.service';
import { MessageService } from '../../../core/service/message.service';
import { NavigationService } from '../../../core/service/navigation.service';
import { SidebarService } from '../../../core/service/sidebar.service';
import { amortizationPlanCommand } from '../../command/amortization-plan';
import { AmortizationPlan } from '../../model/amortization-plan';
import { AmortizationPlanStatus, AmortizationPlanStatusToString } from '../../model/const/enum';
import { AmortizationPlanService } from '../../service/data/amortization-plan.service';
import { Features, ModuleNames } from '../../../common/model/account-definition';
import { UserAccountService } from '../../../common/service/user-account.service';

@Component({
  standalone: false,
  selector: 'app-amortization-plan-manager',
  templateUrl: './amortization-plan-manager.component.html',
  styleUrls: ['./../../../core/component/paged-object-manager/paged-object-manager.scss'],
  providers: [AmortizationPlanService],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class AmortizationPlanManagerComponent extends PagedObjectManagerComponent<AmortizationPlan> {
  @ViewChild('createAmortizationPlanSidebar', { static: false }) createAmortizationPlanSidebar: TemplateRef<any>;
  @ViewChild('createPaymentNoticeSidebar', { static: false }) createPaymentNoticeSidebar: TemplateRef<any>;

  amortizationPlanInvoiceIds: string[] = [];
  selectAmortizationPlanContactFirst = false;
  selectedAmortizationPlanIds: string[] = [];
  private canManageAmortizationPlans = false;

  constructor(
    private readonly _dataService: AmortizationPlanService,
    private readonly _contactService: ContactService,
    private readonly _articleService: ArticleService,
    private readonly _sidebarService: SidebarService,
    accountService: UserAccountService,
    columnFactory: ColumnDefinitionFactory,
    dispatcher: CommandDispatcher,
    handlerResolver: StandardCommandHandlerResolver,
    messageService: MessageService,
    navigationService: NavigationService
  ) {
    super(_dataService, messageService, navigationService, dispatcher, handlerResolver);
    this.columnDefinition = [
      columnFactory.createNumber('AMORTIZATION_PLAN.NUMBER', 'number', ColumnVisibility.mandatory),
      columnFactory.createTranslation('COMMON.STATUS', 'statusCaption', { serverSideSortBinding: 'status' }),
      columnFactory.create('COMPANY.LIST_HEADER', 'companyName'),
      columnFactory.create('COMMON.CONTACT', 'debtorContactName'),
      columnFactory.create('AMORTIZATION_PLAN.INTEREST_ARTICLE', 'articleCaption'),
      columnFactory.createNumber('AMORTIZATION_PLAN.INTEREST_RATE', 'interestRate', ColumnVisibility.optional),
      columnFactory.createMoney('COMMON.AMOUNT', 'totalAmount'),
      columnFactory.createDateTime('COMMON.CREATED_DATE', 'createdAt', ColumnVisibility.default),
      columnFactory.create('COMMON.NOTE', 'note', ColumnVisibility.optional),
    ];
    this.title = 'AMORTIZATION_PLAN.LIST_HEADER';
    this.allowNew = false;
    this.preferencesKey = 'invoice.amortization-plan.list';
    this.AddSubscription(
      accountService.current.subscribe(account => {
        this.canManageAmortizationPlans = account.hasAccess(ModuleNames.Invoicing, Features.ManageInvoices);
        this.allowNew = this.canManageAmortizationPlans;

        if (this.canManageAmortizationPlans) {
          this.addContextMenuItemDefinition('AMORTIZATION_PLAN.PAYMENT_NOTICE.COMMAND', amortizationPlanCommand.createPaymentNotices);
          this.addMenuItemDefinition('AMORTIZATION_PLAN.PAYMENT_NOTICE.COMMAND', amortizationPlanCommand.createPaymentNotices);
        }
      })
    );
  }

  override canExecute(command: CommandBase): boolean {
    if (!this.canManageAmortizationPlans && command.id === amortizationPlanCommand.createPaymentNotices) {
      return false;
    }

    if (command.id === amortizationPlanCommand.createPaymentNotices) {
      if (command instanceof Command) {
        return !command.contextId || command.contextId === this.contextId;
      }

      if (command instanceof FilteredCommand) {
        return command.totalAffectedItemsControlValue > 0;
      }

      return false;
    }

    return super.canExecute(command);
  }

  override execute(command: CommandBase): void {
    if (command.id === amortizationPlanCommand.createPaymentNotices) {
      if (command instanceof Command && command.context?.id) {
        this.openCreatePaymentNoticeSidebar([command.context.id]);
      } else if (command instanceof FilteredCommand) {
        if (command.includedIds?.length > 0) {
          this.openCreatePaymentNoticeSidebar(command.includedIds);
        } else {
          this.messageService.showMessage('AMORTIZATION_PLAN.PAYMENT_NOTICE.FILTERED_SELECTION_NOT_SUPPORTED');
        }
      }

      return;
    }

    super.execute(command);
  }

  ngOnInit() {
    this.AddSubscription(
      forkJoin({
        definition: this._dataService.getFilterDefinition(),
        articles: this._articleService.getList(),
      }).subscribe({
        next: ({ definition, articles }) => {
          this.defaultFilters = definition.filter.defaultFields;
          this.pageSize = definition.pageSize || this.pageSize;

          const companySelection = (definition.companies ?? [])
            .slice()
            .sort((left: Company, right: Company) => left.toString().localeCompare(right.toString()))
            .map(company => ({ caption: company.toString(), value: company.id }));
          companySelection.splice(0, 0, { caption: 'ALL_TEXT', value: '' });

          const articleSelection = articles
            .slice()
            .sort((left: Article, right: Article) => left.toString().localeCompare(right.toString()))
            .map(article => ({ caption: article.toString(), value: article.id }));
          articleSelection.splice(0, 0, { caption: 'ALL_TEXT', value: '' });

          this.filterDefinition = [
            new FilterDefinitionItem(
              'COMMON.CONTACT',
              'debtorContactId',
              definition.filter.debtorContactId,
              this._contactService,
              definition.filter.selectedFields.includes('debtorContactId')
            ),
            new FilterDefinitionItem(
              'AMORTIZATION_PLAN.INTEREST_ARTICLE',
              'articleId',
              definition.filter.articleId,
              articleSelection,
              definition.filter.selectedFields.includes('articleId')
            ),
            new FilterDefinitionItem(
              'COMPANY.LIST_HEADER',
              'companyId',
              definition.filter.companyId,
              companySelection,
              definition.filter.selectedFields.includes('companyId')
            ),
            new FilterDefinitionItem(
              'COMMON.STATUS',
              'status',
              definition.filter.status,
              [
                { caption: 'ALL_TEXT', value: null },
                {
                  caption: AmortizationPlanStatusToString(AmortizationPlanStatus.Draft),
                  value: AmortizationPlanStatus.Draft,
                },
                {
                  caption: AmortizationPlanStatusToString(AmortizationPlanStatus.Approved),
                  value: AmortizationPlanStatus.Approved,
                },
                {
                  caption: AmortizationPlanStatusToString(AmortizationPlanStatus.Completed),
                  value: AmortizationPlanStatus.Completed,
                },
              ],
              definition.filter.selectedFields.includes('status')
            ),
            new FilterDefinitionItemLocalDate(
              'COMMON.CREATED_DATE_FROM',
              'createdFrom',
              definition.filter.createdFrom,
              definition.filter.selectedFields.includes('createdFrom')
            ),
            new FilterDefinitionItemLocalDate(
              'COMMON.CREATED_DATE_TO',
              'createdTo',
              definition.filter.createdTo,
              definition.filter.selectedFields.includes('createdTo')
            ),
          ];
        },
        error: error => {
          this.messageService.showError(error);
        },
      })
    );
  }

  override onCreateItem(): void {
    if (!this.canManageAmortizationPlans) {
      return;
    }

    this.openCreateAmortizationPlanSidebar();
  }

  closeAmortizationPlanSidebar(): void {
    this._sidebarService.toggleSidebar(false);
  }

  onAmortizationPlanCreated(): void {
    this.closeAmortizationPlanSidebar();
    this.reloadCurrentList();
  }

  closePaymentNoticeSidebar(): void {
    this._sidebarService.toggleSidebar(false);
  }

  onPaymentNoticesCreated(): void {
    this.closePaymentNoticeSidebar();
    this.reloadCurrentList();
  }

  private openCreateAmortizationPlanSidebar(): void {
    this.amortizationPlanInvoiceIds = [];
    this.selectAmortizationPlanContactFirst = true;
    this._sidebarService.setSidebarContent({
      template: this.createAmortizationPlanSidebar,
      onClose: () => {
        this.amortizationPlanInvoiceIds = [];
        this.selectAmortizationPlanContactFirst = false;
      },
    });
    this._sidebarService.toggleSidebar(true);
  }

  private openCreatePaymentNoticeSidebar(amortizationPlanIds: string[]): void {
    if (!this.canManageAmortizationPlans) {
      return;
    }

    this.selectedAmortizationPlanIds = (amortizationPlanIds ?? []).filter(id => !!id);
    this._sidebarService.setSidebarContent({
      template: this.createPaymentNoticeSidebar,
      onClose: () => {
        this.selectedAmortizationPlanIds = [];
      },
    });
    this._sidebarService.toggleSidebar(true);
  }

  private reloadCurrentList(): void {
    const selectedFields = this.filterComponent?.filters?.filter(filter => filter.selected).map(filter => filter.name) ?? this.filterState?.selectedFields;
    const filter = {
      ...(this.filterComponent?.form?.value ?? this.filterState ?? this.getPagingChangedFallbackFilter()),
      selectedFields: selectedFields ?? this.defaultFilters ?? [],
    };

    (this as any).sendPageFilter(filter);
  }
}
